import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Cloud as CloudIcon,
  AutoFixHigh as AutoFixHighIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material';
import { rangeosEnvironmentService, RangeOSEnvironment } from '../services/rangeos-environment-api';
import { keycloakApi, KeycloakConfig } from '../services/integrations-api';

export const RangeOSEnvironmentManager: React.FC = () => {
  const [environments, setEnvironments] = useState<RangeOSEnvironment[]>([]);
  const [keycloakInstances, setKeycloakInstances] = useState<KeycloakConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEnvironment, setEditingEnvironment] = useState<RangeOSEnvironment | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [devOpsApiUrl, setDevOpsApiUrl] = useState('');
  const [playerUrl, setPlayerUrl] = useState('');
  const [graphqlUrl, setGraphqlUrl] = useState('');
  const [graphqlSubscriptionsUrl, setGraphqlSubscriptionsUrl] = useState('');
  const [description, setDescription] = useState('');
  const [keycloakInstanceId, setKeycloakInstanceId] = useState<string>('');

  useEffect(() => {
    loadEnvironments();
    loadKeycloakInstances();
  }, []);

  const loadKeycloakInstances = async () => {
    try {
      const instances = await keycloakApi.getAllInstances();
      setKeycloakInstances(instances);
    } catch (err) {
      console.error('Failed to load Keycloak instances:', err);
      // Don't show error - Keycloak is optional
    }
  };

  const loadEnvironments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await rangeosEnvironmentService.getEnvironments();
      setEnvironments(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load RangeOS environments');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (environment?: RangeOSEnvironment) => {
    if (environment) {
      setEditingEnvironment(environment);
      setName(environment.name);
      setDevOpsApiUrl(environment.devOpsApiUrl);
      setPlayerUrl(environment.playerUrl || '');
      setGraphqlUrl(environment.graphqlUrl || '');
      setGraphqlSubscriptionsUrl(environment.graphqlSubscriptionsUrl || '');
      setDescription(environment.description || '');
      setKeycloakInstanceId(environment.keycloakInstanceId || '');
    } else {
      setEditingEnvironment(null);
      setName('');
      setDevOpsApiUrl('');
      setPlayerUrl('');
      setGraphqlUrl('');
      setGraphqlSubscriptionsUrl('');
      setDescription('');
      setKeycloakInstanceId('');
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEnvironment(null);
  };

  const handleSave = async () => {
    try {
      setError(null);

      const data: any = {
        name,
        devOpsApiUrl,
      };

      // Only include optional fields if they have values
      if (playerUrl) data.playerUrl = playerUrl;
      if (graphqlUrl) data.graphqlUrl = graphqlUrl;
      if (graphqlSubscriptionsUrl) data.graphqlSubscriptionsUrl = graphqlSubscriptionsUrl;
      if (description) data.description = description;

      // Always include keycloakInstanceId - send null to clear it, or the actual value
      data.keycloakInstanceId = keycloakInstanceId || null;

      // Preserve status when editing (don't overwrite it)
      if (editingEnvironment && editingEnvironment.status) {
        data.status = editingEnvironment.status;
      }

      console.log('Saving RangeOS environment with data:', data);

      if (editingEnvironment) {
        await rangeosEnvironmentService.updateEnvironment(editingEnvironment.id, data);
      } else {
        await rangeosEnvironmentService.createEnvironment(data);
      }

      handleCloseDialog();
      loadEnvironments();
    } catch (err: any) {
      setError(err.message || 'Failed to save environment');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this RangeOS environment?')) {
      return;
    }

    try {
      setError(null);
      await rangeosEnvironmentService.deleteEnvironment(id);
      loadEnvironments();
    } catch (err: any) {
      setError(err.message || 'Failed to delete environment');
    }
  };

  const handleToggleStatus = async (environment: RangeOSEnvironment) => {
    try {
      setError(null);
      const newStatus = environment.status === 'active' ? 'inactive' : 'active';
      await rangeosEnvironmentService.updateEnvironment(environment.id, {
        status: newStatus,
      });
      loadEnvironments();
    } catch (err: any) {
      setError(err.message || 'Failed to update environment status');
    }
  };

  const handleAutoPopulateUrls = () => {
    if (!name) {
      setError('Please enter an environment name first');
      return;
    }

    // Assume name is the full domain (e.g., "develop-cp.rangeos.engineering")
    // Populate all URL fields with pattern: <service>.<domain>
    const domain = name.trim();
    setDevOpsApiUrl(`https://rangeos-api.${domain}`);
    setPlayerUrl(`https://cpt-player.${domain}`);
    setGraphqlUrl(`https://rangeos-graphql.${domain}/`);
    setGraphqlSubscriptionsUrl(`wss:////rangeos-graphql.${domain}`);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">RangeOS Environments</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Environment
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : environments.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary" align="center">
              No RangeOS environments configured. Click "Add Environment" to get started.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {environments.map((environment) => (
            <Card key={environment.id}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CloudIcon color="primary" />
                      <Typography variant="h6">{environment.name}</Typography>
                      <Chip
                        label={environment.status}
                        color={environment.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>

                    {environment.description && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {environment.description}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>DevOps API:</strong> {environment.devOpsApiUrl}
                      </Typography>
                      {environment.playerUrl && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Player URL:</strong> {environment.playerUrl}
                        </Typography>
                      )}
                      {environment.graphqlUrl && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>GraphQL:</strong> {environment.graphqlUrl}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      color={environment.status === 'active' ? 'success' : 'default'}
                      onClick={() => handleToggleStatus(environment)}
                      title={environment.status === 'active' ? 'Mark as Inactive' : 'Mark as Active'}
                    >
                      {environment.status === 'active' ? <ActiveIcon /> : <InactiveIcon />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(environment)}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(environment.id)}
                      title="Delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingEnvironment ? 'Edit RangeOS Environment' : 'Add RangeOS Environment'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="info" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Authentication:</strong> RangeOS environments use the same Keycloak credentials as this application.
                You don't need to configure separate authentication tokens.
              </Typography>
              <Typography variant="body2">
                <strong>Quick Setup:</strong> Enter a full domain (e.g., "develop-cp.rangeos.engineering", "test-cp.rangeos.engineering")
                and click "Auto-populate URLs" to generate standard RangeOS URL patterns.
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <TextField
                label="Environment Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
                helperText="e.g., develop-cp.rangeos.engineering, test-cp.rangeos.engineering"
              />
              <Button
                variant="outlined"
                startIcon={<AutoFixHighIcon />}
                onClick={handleAutoPopulateUrls}
                sx={{ mt: 0.5, minWidth: 180 }}
              >
                Auto-populate URLs
              </Button>
            </Box>

            <TextField
              label="DevOps API URL"
              value={devOpsApiUrl}
              onChange={(e) => setDevOpsApiUrl(e.target.value)}
              placeholder="https://rangeos-api.test-cp.rangeos.engineering"
              fullWidth
              required
              helperText="Example: https://rangeos-api.test-cp.rangeos.engineering"
            />

            <TextField
              label="Player URL"
              value={playerUrl}
              onChange={(e) => setPlayerUrl(e.target.value)}
              placeholder="https://cpt-player.test-cp.rangeos.engineering"
              fullWidth
              helperText="Example: https://cpt-player.test-cp.rangeos.engineering"
            />

            <TextField
              label="GraphQL URL"
              value={graphqlUrl}
              onChange={(e) => setGraphqlUrl(e.target.value)}
              placeholder="https://rangeos-graphql.test-cp.rangeos.engineering/"
              fullWidth
              helperText="Example: https://rangeos-graphql.test-cp.rangeos.engineering/"
            />

            <TextField
              label="GraphQL Subscriptions URL"
              value={graphqlSubscriptionsUrl}
              onChange={(e) => setGraphqlSubscriptionsUrl(e.target.value)}
              placeholder="wss://rangeos-graphql.test-cp.rangeos.engineering"
              fullWidth
              helperText="Example: wss://rangeos-graphql.test-cp.rangeos.engineering"
            />

            <FormControl fullWidth>
              <InputLabel id="keycloak-instance-label">Keycloak Instance (Optional)</InputLabel>
              <Select
                labelId="keycloak-instance-label"
                value={keycloakInstanceId}
                onChange={(e) => setKeycloakInstanceId(e.target.value)}
                label="Keycloak Instance (Optional)"
              >
                <MenuItem value="">None (No OAuth)</MenuItem>
                {keycloakInstances.map((instance) => (
                  <MenuItem key={instance.id} value={instance.id}>
                    {instance.name} ({instance.realm})
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                Optional: Associate a Keycloak instance for OAuth authentication with this environment
              </Typography>
            </FormControl>

            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={2}
              fullWidth
              helperText="Optional notes about this environment"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!name || !devOpsApiUrl}
          >
            {editingEnvironment ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
